import React from 'react'
import { IMinerNode } from '@/types/miner.interface'
import { useTranslations } from 'next-intl'

import getHashrateValue from '@/utils/getHashrateValue'
import { useAddToCart } from '@/hooks/useAddToCart'

import { Button, Flex, Stack, Text, VStack, Box } from '@chakra-ui/react'

import { formatPrice } from '@/utils/formatPrice'
import { format, parseISO } from 'date-fns'
import { capitalizeTheFirstLetterOfEachWord } from '@/utils/capitalizeFirstLetterOfEachWord'

type Props = {
  hardwareItem: IMinerNode
}

const FacilityMobileDetailCard = function ({ hardwareItem }: Props) {
  const t = useTranslations()
  const [alterCartMutation, { loading: alterCartLoading }] = useAddToCart({
    productId: hardwareItem.productId,
    redirectToCheckout: true,
  })
  const getAvailableStock = (stock: number) => {
    if (stock > 10) {
      return '10+'
    }
    return stock
  }

  const isNonhostedProduct = (item: IMinerNode) => {
    if (item.productType === 'NON_HOSTED') {
      return true
    }
    return false
  }

  return (
    <VStack
      flexDirection={'column'}
      w={'full'}
      border={'2px solid #e6e6e6'}
      borderRadius={'lg'}
      p={'8'}
      mb={'6'}
      spacing={'4'}
    >
      <Flex
        fontSize={'xs'}
        w="full"
        justify={'space-between'}
        alignItems={'center'}
      >
        <Text>Miner</Text>
        <Text>{hardwareItem.minerModelName || 'N/A'}</Text>
      </Flex>
      <Flex fontSize={'xs'} w="full" justify={'space-between'} align="center">
        <Text>{t('hardware-list.labels.hosting-fee')}</Text>
        <Box>
          {!isNonhostedProduct(hardwareItem) ? (
            <Text>
              {formatPrice(
                hardwareItem?.overriddenMonthlyHostingCharges ||
                  hardwareItem?.monthlyHostingCharges,
                true
              )}
            </Text>
          ) : (
            <Text
              textAlign="center"
              fontWeight={'normal'}
              whiteSpace={'nowrap'}
              color="gray.500"
            >
              {t('common.labels.not-applicable')}
            </Text>
          )}
        </Box>
      </Flex>
      <Flex fontSize={'xs'} w="full" justify={'space-between'} align="center">
        <Text>Estimated Online Date</Text>
        {isNonhostedProduct(hardwareItem) ? (
          <Text>{hardwareItem?.nonhostedShippingTimeDescription || ''}</Text>
        ) : (
          <Text>
            {hardwareItem?.hostedTargetOnlineDate
              ? format(
                  parseISO(hardwareItem.hostedTargetOnlineDate),
                  'MM/dd/yyyy'
                )
              : '-'}
          </Text>
        )}
      </Flex>
      <Flex fontSize={'xs'} w="full" justify={'space-between'} align="center">
        <Text>{t('common.labels.condition')}</Text>
        <Text>
          {capitalizeTheFirstLetterOfEachWord(
            hardwareItem?.productCondition?.replace('_', ' ')
          )}
        </Text>
      </Flex>
      <Flex fontSize={'xs'} w="full" justify={'space-between'} align="center">
        <Text>{t('common.labels.hashrate')}</Text>
        <Text>
          {getHashrateValue(
            hardwareItem.minerModelAlgorithm,
            Number(hardwareItem.minerModelHashratePerTerahash),
            false
          )}
        </Text>
      </Flex>
      <Flex fontSize={'xs'} w="full" justify={'space-between'} align="center">
        <Text>{t('common.labels.power')}</Text>
        <Text>{`${hardwareItem.minerModelPowerConsumptionWatts.toLocaleString()} Watts`}</Text>
      </Flex>
      <Flex fontSize={'xs'} w="full" justify={'space-between'} align="center">
        <Text>{t('hardware-list.labels.available-quantity')}</Text>
        <Text>
          {`${getAvailableStock(hardwareItem.productAvailableQuantity)}`}
        </Text>
      </Flex>
      <Flex
        fontSize={'xs'}
        w="full"
        justifyItems={'space-between'}
        align="center"
      >
        <Text>{`$/${t('common.labels.hashrate')}`}</Text>
        <Flex w="full" justify={'right'} alignItems={'center'}>
          <Text>
            {getHashrateValue(
              hardwareItem?.minerModelAlgorithm,
              Number(hardwareItem?.productPricePerHashrate),
              true
            )}
          </Text>
        </Flex>
      </Flex>
      <Flex
        fontSize={'xs'}
        w="full"
        justifyItems={'space-between'}
        align="center"
      >
        <Text>{t('common.labels.price')}</Text>
        <Flex w="full" justify={'right'} alignItems={'center'}>
          <Text fontSize={'lg'}>
            {formatPrice(hardwareItem.productListingPrice, false)}
          </Text>
        </Flex>
      </Flex>
      <Stack w={{ base: 'full', md: '7/10' }} justifyItems={'center'} m="auto">
        <Button
          variant="black"
          onClick={() => {
            if (hardwareItem.productAvailableQuantity > 0) {
              alterCartMutation()
            }
          }}
          isLoading={alterCartLoading}
          className="gtm-buy-now"
        >
          {t('buttons.buy-now')}
        </Button>
      </Stack>
    </VStack>
  )
}

export default FacilityMobileDetailCard
